import { Plus, Search } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomAvatar } from "@/components/common/CustomAvatar";
import { ListItem } from "@/components/common/ListItem";
import { UserAvatarWithName } from "@/components/common/UserAvatarWithName";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useChatRoomsForFilter } from "@/hooks/useChatRoomsForFilter";
import { useSearchChatEntries } from "@/hooks/useSearchChatEntries";
import { useSearchChatRooms } from "@/hooks/useSearchChatRooms";
import type { User } from "@/lib/humhub-api/types";
import { formatTimeLocal } from "@/lib/utils/dateUtils";

interface ChatSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChatRoomSelect: (roomId: number | undefined) => void;
  onCreateNewChat?: (selectedUser?: User) => void;
  initialUser?: User;
}

export const ChatSearchModal = ({
  isOpen,
  onClose,
  onChatRoomSelect,
  onCreateNewChat,
  initialUser,
}: ChatSearchModalProps) => {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState("");
  const [, setSearchKeyword] = useState("");
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(initialUser);
  const isSearching = useRef(false);

  // 検索用チャットルーム一覧取得
  const {
    allChatRoomIds,
    isLoading: isLoadingAllRooms,
    isDataComplete,
    error: chatRoomsError,
  } = useChatRoomsForFilter();

  // チャットルーム検索用のフック
  const {
    isLoading: isLoadingRooms,
    error: roomsError,
    searchChatRooms,
    response: chatRoomSearchResponse,
  } = useSearchChatRooms();

  // チャットメッセージ検索
  const {
    isLoading: isLoadingEntries,
    error: entriesError,
    searchChatEntries,
    response: chatEntries,
  } = useSearchChatEntries();

  // initialUserが変更されたらselectedUserを更新
  useEffect(() => {
    setSelectedUser(initialUser);
    if (initialUser) {
      const userName = `${initialUser.profile.firstname} ${initialUser.profile.lastname}`.trim();
      setKeyword(userName);
    }
  }, [initialUser]);

  // キーワードが変更されたら検索をリセット
  // biome-ignore lint/correctness/useExhaustiveDependencies: 必要な場合のみ再実行
  useEffect(() => {
    if (isOpen) {
      setSearchPerformed(false);
    }
  }, [keyword, isOpen]);

  // モーダルの開閉状態管理
  useEffect(() => {
    if (isOpen) {
      if (!initialUser) {
        setKeyword("");
        setSelectedUser(undefined);
      }
      setSearchKeyword("");
      setSearchPerformed(false);
      isSearching.current = false;
    } else {
      // モーダルが閉じた時のリセット
      isSearching.current = false;
      setKeyword("");
      setSelectedUser(undefined);
      setSearchKeyword("");
      setSearchPerformed(false);
    }
  }, [isOpen, initialUser]);

  // 検索実行関数
  const performSearch = useCallback(async () => {
    const searchTerm = keyword.trim();
    if (!searchTerm || isSearching.current || !isDataComplete) return;

    // チャットルームが存在しない場合でも検索実行済みとしてマーク
    if (allChatRoomIds.length === 0) {
      console.log("No chat room IDs available for search");
      setSearchPerformed(true);
      return;
    }

    isSearching.current = true;
    setSearchKeyword(searchTerm);
    setSearchPerformed(true);

    try {
      // チャットルーム検索
      try {
        await searchChatRooms(searchTerm, allChatRoomIds);
      } catch (roomError) {
        console.error("チャットルーム検索エラー:", roomError);
      }

      // チャットエントリー検索
      try {
        await searchChatEntries(searchTerm, allChatRoomIds);
      } catch (entryError) {
        console.error("チャットエントリー検索エラー:", entryError);
      }
    } catch (error) {
      console.error("検索失敗:", error);
    } finally {
      isSearching.current = false;
    }
  }, [keyword, searchChatRooms, searchChatEntries, allChatRoomIds, isDataComplete]);

  // 検索ボタンクリックハンドラ
  const handleSearch = () => {
    performSearch();
  };

  // Enterキー押下ハンドラ
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      performSearch();
    }
  };

  // 新規チャット作成ハンドラ
  const handleCreateNewChat = () => {
    if (onCreateNewChat) {
      onCreateNewChat(selectedUser);
    }
    onClose();
  };

  const handleChatRoomClick = (roomId: number | undefined) => {
    onChatRoomSelect(roomId);
    onClose();
  };

  const isLoading = isLoadingRooms || isLoadingEntries || isLoadingAllRooms;
  const hasError = roomsError || entriesError || chatRoomsError;
  const chatRooms = chatRoomSearchResponse?.results || [];
  const noResults =
    searchPerformed &&
    !isLoading &&
    !hasError &&
    chatRooms.length === 0 &&
    (!chatEntries || !chatEntries.results || chatEntries.results.length === 0);

  // データ準備中の表示
  const isPreparingData = isLoadingAllRooms || !isDataComplete;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("chat.searchTitle")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("chat.searchPlaceholder")}
              className="flex-1"
              disabled={isPreparingData}
            />
            <Button
              onClick={handleSearch}
              disabled={!keyword.trim() || isLoading || isSearching.current || isPreparingData}
              variant="ghost"
            >
              <Search size={18} />
            </Button>
          </div>

          {isPreparingData && (
            <div className="py-8 text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-[#377E7F] border-b-2" />
              <p className="mt-2 text-gray-500 text-sm">{t("common.loading")}</p>
            </div>
          )}

          {isLoading && !isPreparingData && (
            <div className="py-8 text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-[#377E7F] border-b-2" />
              <p className="mt-2 text-gray-500 text-sm">{t("common.searching")}</p>
            </div>
          )}

          {hasError && (
            <div className="py-4 text-center text-red-500">
              {roomsError || entriesError || chatRoomsError}
            </div>
          )}

          {noResults && (
            <div className="py-4 text-center text-gray-500">{t("chat.noSearchResults")}</div>
          )}

          {searchPerformed && !isLoading && !hasError && !isPreparingData && (
            <div className="max-h-[400px] space-y-4 overflow-y-auto">
              {/* チャットルーム検索結果 */}
              {chatRooms.length > 0 && (
                <div>
                  <h3 className="mb-2 font-medium">{t("chat.roomResults")}</h3>
                  <div className="space-y-1">
                    {chatRooms.map((room) => (
                      <ListItem
                        key={`room-${room.id}`}
                        icon={
                          <CustomAvatar name={room.title} imageUrl={room.image_url} size={32} />
                        }
                        label={room.title}
                        sublabel={room.display_title}
                        onClick={() => handleChatRoomClick(room.id)}
                        hasNewEntry={room.has_new_entry}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* チャットメッセージ検索結果 */}
              {chatEntries?.results && chatEntries.results.length > 0 && (
                <div>
                  <h3 className="mb-2 font-medium">{t("chat.messageResults")}</h3>
                  <div className="space-y-1">
                    {chatEntries.results.map((entry) => {
                      const createdAt = new Date(entry.created_at);
                      return (
                        <div
                          key={`entry-${entry.id}`}
                          className="cursor-pointer rounded-md p-2 hover:bg-gray-100"
                          onClick={() => handleChatRoomClick(entry.message_id)}
                        >
                          <div className="mb-1 flex items-center gap-2">
                            <UserAvatarWithName user={entry.user} size="sm" />
                            <span className="text-gray-500 text-xs">
                              {createdAt.toLocaleDateString()} {formatTimeLocal(createdAt)}
                            </span>
                          </div>
                          {entry.message_title && (
                            <div className="mb-1 pl-8">
                              <span className="rounded-md bg-gray-100 px-2 py-1 text-xs">
                                {entry.message_title}
                              </span>
                            </div>
                          )}
                          <p className="line-clamp-2 overflow-hidden break-all pl-8 text-sm">
                            {entry.content}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {/* 新規チャット作成ボタン */}
          {onCreateNewChat && (
            <Button onClick={handleCreateNewChat} variant="teal">
              <Plus size={16} className="mr-2" />
              {t("chat.createNewChatRoom")}
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            {t("common.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
